import { useState, useEffect } from "react";
import { useForm } from "@refinedev/antd";
import type { HttpError } from "@refinedev/core";
import { useGo, useInvalidate } from "@refinedev/core";
import { useParams } from "react-router-dom";

import { Card, Col, Form, Input, Row, Select, Space, Button, InputNumber, Typography } from "antd";
import { api } from "@/providers/rest";
import { resolveApiBase } from "@/providers/api-base";

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

interface KBDocument {
  id: string;
  title: string;
  document_type: string;
  content: string;
  contextualized_content?: string;
  product_info?: {
    seller_sku?: string;
    wb_sku?: string;
    category?: string;
    color?: string | null;
    barcode?: string;
    photo_url?: string;
    price?: number | string;
    old_price?: number | string;
    characteristics?: Record<string, any>;
    attributes?: Record<string, any>;
  };
  tags: string[];
  metadata: any;
  is_indexed: boolean;
  created_at: string;
  updated_at: string;
}

interface KBFormValues {
  title: string;
  document_type: string;
  content: string;
  contextualized_content?: string;
  product_info?: {
    seller_sku?: string;
    wb_sku?: string;
    category?: string;
    color?: string | null;
    barcode?: string;
    photo_url?: string;
    price?: number | string;
    old_price?: number | string;
    characteristics?: Record<string, any>;
    attributes?: Record<string, any>;
  };
  tags?: string[];
  metadata?: any;
}

export const KBEditForm = () => {
  const go = useGo();
  const invalidate = useInvalidate();
  const params = useParams();
  const [docType, setDocType] = useState<"product" | "document">("document");
  const [docData, setDocData] = useState<KBDocument | null>(null);
  const [brands, setBrands] = useState<string[]>([]);

  // Загрузка списка брендов
  useEffect(() => {
    api.get<{brands: string[]}>("/reviews/brands")
      .then(res => setBrands(res.brands || []))
      .catch(() => setBrands(["Art-Visage", "Estrade"])); // fallback
  }, []);

  const { formProps, formLoading } = useForm<
    KBDocument,
    HttpError,
    KBFormValues
  >({
    resource: "knowledge-base",
    id: params.id,
    redirect: false,
    mutationMode: "pessimistic",
    onMutationSuccess: () => {
      invalidate({ resource: "knowledge-base", invalidates: ["resourceAll"] });
      go({
        to: "/kb",
        type: "replace",
        options: { keepQuery: true },
      });
    },
    successNotification: () => ({
      type: "success",
      message: "Успешно",
      description: "Документ обновлен",
    }),
    errorNotification: (error) => ({
      type: "error",
      message: "Не удалось сохранить",
      description:
        error?.message || "Проверьте обязательные поля и повторите попытку",
    }),
  });

  // Load document type directly on mount
  useEffect(() => {
    const loadDocumentType = async () => {
      if (!params.id) return;

      try {
        const API_URL = resolveApiBase("");
        const response = await fetch(`${API_URL}/api/v1/knowledge-base/${params.id}`);
        const data = await response.json();

        setDocData(data);
        if (data.document_type) {
          const newDocType = data.document_type === "product_info" ? "product" : "document";
          setDocType(newDocType);
        }
      } catch (error) {
        console.error('[KB Edit] Failed to load document type:', error);
      }
    };

    loadDocumentType();
  }, [params.id]);

  const handleFinish = async (values: KBFormValues) => {
    const payload = {
      ...values,
      title: values.title.trim(),
      tags: values.tags || [],
      metadata: values.metadata || {},
    };

    if (docType === "product") {
      payload.document_type = "product_info";
    }

    await formProps.onFinish?.(payload);
  };

  const cardTitle = docType === "product"
    ? "Редактирование карточки товара"
    : "Редактирование документа базы знаний";

  return (
    <Card title={cardTitle} loading={formLoading} bordered>
      <Form
        {...formProps}
        layout="vertical"
        onFinish={handleFinish}
        autoComplete="off"
      >
        {docType === "product" ? (
          <>
            <Form.Item
              label="Название товара"
              name="title"
              rules={[
                { required: true, message: "Введите название товара" },
                { min: 3, message: "Минимум 3 символа" },
              ]}
            >
              <Input placeholder="Например: Тушь для ресниц Volume" size="large" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Артикул продавца"
                  name={["product_info", "seller_sku"]}
                  rules={[{ required: true, message: "Введите артикул" }]}
                >
                  <Input placeholder="040955" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Артикул WB"
                  name={["product_info", "wb_sku"]}
                >
                  <Input placeholder="56184727" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Бренд"
                  name={["metadata", "brand"]}
                  rules={[{ required: true, message: "Выберите бренд" }]}
                  tooltip="Бренд товара для категоризации в базе знаний"
                >
                  <Select
                    placeholder="Выберите бренд"
                    size="large"
                    options={brands.map(b => ({ value: b, label: b }))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Категория"
                  name={["product_info", "category"]}
                  rules={[{ required: true, message: "Выберите категорию" }]}
                >
                  <Select
                    placeholder="Выберите категорию"
                    size="large"
                    options={[
                      { value: "Туши", label: "Туши" },
                      { value: "Пудры", label: "Пудры" },
                      { value: "Блески", label: "Блески" },
                      { value: "Тональные средства", label: "Тональные средства" },
                      { value: "Помады", label: "Помады" },
                      { value: "Тени", label: "Тени" },
                      { value: "Подводки", label: "Подводки" },
                      { value: "Румяна", label: "Румяна" },
                      { value: "Брови", label: "Брови" },
                      { value: "Консилеры", label: "Консилеры" },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="Цвет"
              name={["product_info", "color"]}
            >
              <Input placeholder="черный, розовый, прозрачный..." />
            </Form.Item>

            <Form.Item
              label="Описание товара (для RAG)"
              name="content"
              rules={[
                { required: true, message: "Введите описание" },
                { min: 10, message: "Минимум 10 символов" },
              ]}
            >
              <TextArea
                rows={6}
                placeholder="Подробное описание товара, характеристики, эффект, применение..."
              />
            </Form.Item>

            <Form.Item
              label="Контекст для RAG (необязательно)"
              name="contextualized_content"
              help="Дополнительный контекст: связи с другими товарами, альтернативы, информация про оттенки"
            >
              <TextArea
                rows={3}
                placeholder='Например: "Доступен в оттенках 101, 102, 104. Для жирной кожи. Водостойкая формула."'
              />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Цена (руб)"
                  name={["product_info", "price"]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="630"
                    min={0}
                    precision={2}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Старая цена (руб)"
                  name={["product_info", "old_price"]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="750"
                    min={0}
                    precision={2}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="Фото товара (URL)"
              name={["product_info", "photo_url"]}
            >
              <Input placeholder="https://example.com/image.jpg" />
            </Form.Item>

            <Form.Item
              label="Баркод"
              name={["product_info", "barcode"]}
            >
              <Input placeholder="4607154721234" />
            </Form.Item>

            <Form.Item
              label="Теги"
              name="tags"
              help="Теги для поиска и категоризации"
            >
              <Select
                mode="tags"
                tokenSeparators={[",", ";"]}
                placeholder="Добавьте теги"
                style={{ width: "100%" }}
              />
            </Form.Item>

            {/* Характеристики WB (только для чтения) */}
            {docData?.product_info?.characteristics && Object.keys(docData.product_info.characteristics).length > 0 && (
              <Card
                title="Характеристики WB (только просмотр)"
                size="small"
                style={{ marginBottom: 16, background: '#fafafa' }}
              >
                <pre style={{
                  maxHeight: '200px',
                  overflow: 'auto',
                  background: '#fff',
                  padding: '8px',
                  borderRadius: '4px',
                  fontSize: '12px'
                }}>
                  {JSON.stringify(docData.product_info.characteristics, null, 2)}
                </pre>
              </Card>
            )}

            {/* Атрибуты Ozon (только для чтения) */}
            {docData?.product_info?.attributes && Object.keys(docData.product_info.attributes).length > 0 && (
              <Card
                title="Атрибуты Ozon (только просмотр)"
                size="small"
                style={{ marginBottom: 16, background: '#fafafa' }}
              >
                <pre style={{
                  maxHeight: '200px',
                  overflow: 'auto',
                  background: '#fff',
                  padding: '8px',
                  borderRadius: '4px',
                  fontSize: '12px'
                }}>
                  {JSON.stringify(docData.product_info.attributes, null, 2)}
                </pre>
              </Card>
            )}
          </>
        ) : (
          <>
            <Form.Item
              label="Заголовок"
              name="title"
              rules={[
                { required: true, message: "Введите заголовок" },
                { min: 3, message: "Минимум 3 символа" },
              ]}
            >
              <Input placeholder="Например: Как вернуть товар" size="large" />
            </Form.Item>

            <Form.Item
              label="Тип документа"
              name="document_type"
              rules={[{ required: true, message: "Выберите тип" }]}
              tooltip="Тип документа определяет его назначение в базе знаний"
            >
              <Select
                placeholder="Выберите тип"
                size="large"
                options={[
                  { value: "faq", label: "FAQ" },
                  { value: "tone_of_voice", label: "Tone of Voice" },
                  { value: "policy", label: "Политика компании" },
                  { value: "instruction", label: "Инструкция" },
                ]}
              />
            </Form.Item>

            <Form.Item
              label="Содержимое"
              name="content"
              rules={[
                { required: true, message: "Введите содержимое" },
                { min: 10, message: "Минимум 10 символов" },
              ]}
            >
              <TextArea
                rows={8}
                placeholder="Подробное содержимое документа..."
              />
            </Form.Item>

            <Form.Item
              label="Теги"
              name="tags"
              help="Теги для поиска"
            >
              <Select
                mode="tags"
                tokenSeparators={[",", ";"]}
                placeholder="Добавьте теги"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </>
        )}

        <Space>
          <Button type="primary" htmlType="submit" loading={formLoading}>
            Сохранить
          </Button>
        </Space>
      </Form>
    </Card>
  );
};
